// GAVION training (« Formation »): the mission catalog. Game missions are stored in public.training_progress
// (46_gavion_training.sql); their pass mark and XP are mirrored by private.training_mission_xp (keep in sync:
// missions.test.ts reads the SQL). The « Produit » quiz missions are stored too (107_gavion_product_training.sql). Field missions are never stored: my_training_status() computes them from
// calls, dial_sessions, deals and sales.prefs, and the client only displays the result.

export const GAME_MISSION_KEYS = [
  "golden_hours",
  "read_queue",
  "right_outcome",
  "hotkeys",
  "crisis",
] as const;
export type GameMissionKey = (typeof GAME_MISSION_KEYS)[number];

/** « Produit » (spec 2026-11-02-product-training-design.md): quiz missions on Gavion's product and offer. */
export const PRODUCT_MISSION_KEYS = [
  "product_pitch",
  "product_pricing",
  "product_addons",
  "product_fit",
  "product_objections",
  "product_availability",
  "product_how_gala",
  "product_setup",
  "product_integrations",
  "product_client_gets",
  "product_admin_assistant",
] as const;
export type ProductMissionKey = (typeof PRODUCT_MISSION_KEYS)[number];
/** A mission with a stored score (training_progress.mission_key). */
export type PlayableKey = GameMissionKey | ProductMissionKey;
/** « Vendre Gavion » (open) then « Gavion à fond » (opens when all of « Vendre Gavion » is passed). */
export type ProductTrack = "sell" | "deep";

export const FIELD_MISSION_KEYS = [
  "ready",
  "first_session",
  "ten_calls",
  "first_contact",
  "first_callback",
  "first_demo",
  "contact_call",
  "zero_unlogged",
] as const;
export type FieldMissionKey = (typeof FIELD_MISSION_KEYS)[number];
export type MissionKey = GameMissionKey | FieldMissionKey | ProductMissionKey;

export type GameKind = "quiz" | "order" | "hotkeys";

export type GameMission = {
  key: PlayableKey;
  type: "game";
  game: GameKind;
  /** Score (0–100) from which the mission counts as done. */
  pass: number;
  xp: number;
  /** Extra XP for a perfect score (100). */
  perfectBonus: number;
  /** Product missions only. */
  track?: ProductTrack;
};
export type FieldMission = {
  key: FieldMissionKey;
  type: "field";
  xp: number;
  /** Count shown as "n / target" while in progress (ten_calls), else 1. */
  target: number;
  /** Where the rep goes to do it. */
  cta: string;
};
export type Mission = GameMission | FieldMission;

/** « 10 appels »: calls logged with one of the 8 dial outcomes. */
export const TEN_CALLS_TARGET = 10;

/** Map order (the path the rep walks). « Prêt à composer » opens it; field missions close it. */
export const MISSIONS: readonly Mission[] = [
  {
    key: "ready",
    type: "field",
    xp: 50,
    target: 1,
    cta: "/parametres/telephone",
  },
  {
    key: "golden_hours",
    type: "game",
    game: "quiz",
    pass: 80,
    xp: 100,
    perfectBonus: 50,
  },
  {
    key: "read_queue",
    type: "game",
    game: "order",
    pass: 66,
    xp: 100,
    perfectBonus: 50,
  },
  {
    key: "right_outcome",
    type: "game",
    game: "quiz",
    pass: 75,
    xp: 150,
    perfectBonus: 50,
  },
  {
    key: "hotkeys",
    type: "game",
    game: "hotkeys",
    pass: 60,
    xp: 100,
    perfectBonus: 0,
  },
  {
    key: "crisis",
    type: "game",
    game: "quiz",
    pass: 80,
    xp: 100,
    perfectBonus: 50,
  },
  { key: "first_session", type: "field", xp: 50, target: 1, cta: "/appeler" },
  {
    key: "ten_calls",
    type: "field",
    xp: 100,
    target: TEN_CALLS_TARGET,
    cta: "/appeler",
  },
  { key: "first_contact", type: "field", xp: 100, target: 1, cta: "/appeler" },
  { key: "first_callback", type: "field", xp: 100, target: 1, cta: "/appeler" },
  { key: "first_demo", type: "field", xp: 150, target: 1, cta: "/appeler" },
  { key: "contact_call", type: "field", xp: 75, target: 1, cta: "/contacts" },
  {
    key: "zero_unlogged",
    type: "field",
    xp: 75,
    target: 1,
    cta: "/appeler/a-completer",
  },
];

const product = (key: ProductMissionKey, track: ProductTrack): GameMission => ({
  key,
  type: "game",
  game: "quiz",
  pass: 80,
  xp: 100,
  perfectBonus: 50,
  track,
});

/** Map order of the « Produit » tab. SQL twin: private.training_mission_xp (107_). */
export const PRODUCT_MISSIONS: readonly GameMission[] =
  PRODUCT_MISSION_KEYS.map((k, i) => product(k, i < 6 ? "sell" : "deep"));

export const PRODUCT_MAX_XP = PRODUCT_MISSIONS.reduce(
  (s, m) => s + m.xp + m.perfectBonus,
  0,
);

export const missionByKey = (key: string): Mission | undefined =>
  MISSIONS.find((m) => m.key === key);

export const gameMission = (key: PlayableKey): GameMission =>
  (MISSIONS.find((m) => m.key === key) ??
    PRODUCT_MISSIONS.find((m) => m.key === key)) as GameMission;

export const isGameMissionKey = (key: string): key is GameMissionKey =>
  (GAME_MISSION_KEYS as readonly string[]).includes(key);
export const isProductMissionKey = (key: string): key is ProductMissionKey =>
  (PRODUCT_MISSION_KEYS as readonly string[]).includes(key);
export const isPlayableKey = (key: string): key is PlayableKey =>
  isGameMissionKey(key) || isProductMissionKey(key);

/** Percentage score, rounded (SQL stores integers 0–100). */
export const scoreOf = (correct: number, total: number): number =>
  total <= 0
    ? 0
    : Math.round((Math.max(0, Math.min(correct, total)) / total) * 100);

/** XP a game mission is worth at this best score. SQL twin: private.training_mission_xp. */
export function gameXp(key: PlayableKey, bestScore: number): number {
  const m = gameMission(key);
  if (bestScore < m.pass) return 0;
  return m.xp + (bestScore >= 100 ? m.perfectBonus : 0);
}

export const MAX_XP = MISSIONS.reduce(
  (sum, m) => sum + m.xp + (m.type === "game" ? m.perfectBonus : 0),
  0,
);
